<template>
  <v-card
    flat
    tile
  >
    <v-card-text>
      <v-row
        class="d-flex align-center mb-4"
        dense
      >
        <v-col>
          <h2
            class="fs-20 fw-600"
            tabindex="0"
          >
            {{ familySubsidyProgram.name }}
          </h2>
        </v-col>
      </v-row>

      <template v-if="familySubsidyProgram.description">
        <v-row dense>
          <v-col>
            <p
              class="c-light-black fs-16 fw-500"
              tabindex="0"
            >
              {{ $bct(familySubsidyProgram.description) }}
            </p>
          </v-col>
        </v-row>
      </template>

      <v-row>
        <v-col cols="12">
          <template v-if="unfinishedSubsidy">
            <v-btn
              @click="continueApplication"
              :block="$vuetify.display.smAndDown"
              append-icon="east"
              class="focus-very-visible wrap-text"
              color="primary"
              data-cy="continue-button"
              tile
            >
              {{ continueButtonText }}
            </v-btn>
          </template>
          <template v-else-if="canCreateSubsidies">
            <v-btn
              @click="$emit('create', familySubsidyProgram)"
              :block="$vuetify.display.smAndDown"
              :loading="processing"
              append-icon="east"
              class="focus-very-visible wrap-text"
              color="primary"
              data-cy="re-apply-button"
              tile
            >
              {{ startButtonText }}
            </v-btn>
          </template>
        </v-col>
      </v-row>

      <template
        v-if="familySubsidies.filter((familySubsidy) => familySubsidy.submitted_at).length > 0"
      >
        <v-divider class="my-4" />
      </template>

      <div
        v-for="subsidy in familySubsidies.filter((familySubsidy) => familySubsidy.submitted_at)"
        :key="subsidy.id"
      >
        <v-row
          class="mb-2"
          dense
        >
          <v-col class="d-flex align-center justify-start">
            <v-icon
              class="material-icons-outlined"
              color="primary"
              icon="check_circle"
              size="24"
              start
            />

            <LongDateTime
              :date="subsidy.submitted_at"
              data-cy="app_submitted"
              prefix="Application submitted at"
              tabindex="0"
            />
          </v-col>

          <v-col class="d-flex justify-end">
            <div class="d-grid">
              <v-btn
                @click="reviewApplication(subsidy)"
                append-icon="east"
                class="focus-very-visible wrap-text"
                color="primary"
              >
                {{ reviewButtonText }}
              </v-btn>

              <template v-if="canViewClaims(subsidy)">
                <v-btn
                  @click="reviewApplication(subsidy, 'Claims')"
                  append-icon="east"
                  class="mt-1"
                  color="primary"
                  variant="outlined"
                >
                  {{ $bct('Claims') }}
                </v-btn>
              </template>
            </div>
          </v-col>
        </v-row>

        <v-divider class="mt-4" />
      </div>
    </v-card-text>
  </v-card>
</template>

<script setup>
import LongDateTime from '@/shared/components/LongDateTime.vue';
import { useDisplay } from 'vuetify';
import { useRouter } from 'vue-router';

const props = defineProps({
  familySubsidies: {
    type: Array,
    default: null,
  },
  familySubsidyProgram: {
    type: Object,
    default: null,
  },
  processing: {
    type: Boolean,
  },
});

defineEmits(['create']);

const display = useDisplay();
const router = useRouter();
const $bct = inject('translator');

const canCreateSubsidies = computed(() => {
  return (
    props.familySubsidies.length === 0 || props.familySubsidyProgram.enable_multiple_applications
  );
});

const continueButtonText = computed(() => {
  if (display.smAndDown) return $bct('Continue');

  return `${$bct('Continue')} ${props.familySubsidyProgram.name}`;
});

const reviewButtonText = computed(() => {
  if (display.smAndDown) return $bct('Review');

  return `${$bct('Review')} ${props.familySubsidyProgram.name}`;
});

const startButtonText = computed(() => {
  if (display.smAndDown) return $bct('Start');

  return `${$bct('Start')} ${props.familySubsidyProgram.name}`;
});

const unfinishedSubsidy = computed(() => {
  return props.familySubsidies.filter((familySubsidy) => !familySubsidy.submitted_at)[0];
});

function canViewClaims(subsidy) {
  // TO-DO : Once family subsidy status is being passed forward include "&& subsidy.status === 'Approved'" (BCD-1067)
  return (
    !!subsidy.submitted_at &&
    props.familySubsidyProgram.enable_claims &&
    props.familySubsidyProgram?.claim_schema_id
  );
}

function continueApplication() {
  router.push({
    name: 'FamilySubsidyContinue',
    params: { id: unfinishedSubsidy.value.id },
  });
}

function reviewApplication(subsidy, tab) {
  router.push({
    name: 'FamilySubsidyReview',
    params: { id: subsidy.id },
    query: { tab },
  });
}
</script>
